import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

import axiosInstance from '../services/axios.instance';

// ────────────────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────────────────

export type FormaPago = 'EFECTI' | 'TDEBI' | 'TCRED' | 'TRANS';

export interface DetallePago {
    formaPago: FormaPago;
    valor: number;
    referencia?: string;       // últimos 4 dígitos o nro. transferencia
    cuotas?: number;           // solo tarjeta crédito
    valorRecibido?: number;    // solo efectivo (para calcular vuelto)
}

export interface MovimientoCaja {
    id: string;
    fecha: string;             // ISO date YYYY-MM-DD
    hora: string;              // HH:mm
    concepto: string;
    paciente: string;
    citaId?: number;
    totalCobrado: number;
    detallesPago: DetallePago[];
}

export interface AperturaCaja {
    monto: number;
    hora: string;
    fecha: string;
    usuario: string;
    observaciones?: string;
    idApertura?: number;       // id del backend (idcaja)
    idcaja?: number;
    idsede?: number;
}

interface CajaState {
    cajaAbierta: boolean;
    apertura: AperturaCaja | null;
    movimientos: MovimientoCaja[];
}

interface CajaContextValue extends CajaState {
    abrirCaja: (monto: number, observaciones?: string, bancoini?: number) => Promise<void>;
    cerrarCaja: () => Promise<void>;
    registrarPago: (movimiento: Omit<MovimientoCaja, 'id' | 'fecha' | 'hora'>) => Promise<MovimientoCaja>;
    loadingCaja: boolean;
    verificarCajaServidor: () => Promise<void>;
}

// ────────────────────────────────────────────────────────────────────────────
// Constants
// ────────────────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'caja_mde_estado';

const estadoInicial: CajaState = {
    cajaAbierta: false,
    apertura: null,
    movimientos: [],
};

// ────────────────────────────────────────────────────────────────────────────
// Context
// ────────────────────────────────────────────────────────────────────────────

const CajaContext = createContext<CajaContextValue | null>(null);

export const useCaja = (): CajaContextValue => {
    const ctx = useContext(CajaContext);
    if (!ctx) throw new Error('useCaja debe usarse dentro de <CajaProvider>');
    return ctx;
};

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

const genId = () => `mov_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`;
const nowTime = () => new Date().toTimeString().slice(0, 5);
const nowDate = () => new Date().toISOString().slice(0, 10);

// ────────────────────────────────────────────────────────────────────────────
// Provider
// ────────────────────────────────────────────────────────────────────────────

export const CajaProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [estado, setEstado] = useState<CajaState>(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (raw) return JSON.parse(raw) as CajaState;
        } catch {
            // ignore parse errors
        }
        return estadoInicial;
    });

    const [loadingCaja, setLoadingCaja] = useState(false);

    // Persist to localStorage on every state change
    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(estado));
    }, [estado]);

    // ── Verificar caja en servidor ─────────────────────────────────────────
    const verificarCajaServidor = useCallback(async () => {
        try {
            const idempresa = Number(localStorage.getItem('auth_mde_emp')) || 1;
            const idsede = Number(localStorage.getItem('auth_mde_sede')) || 1;

            const res = await axiosInstance.post('/caja/abierta', {
                idempresa,
                idsede
            });

            // Soportar diferentes formatos de respuesta del backend: array directo, { data: [...] }, objeto simple, etc.
            let rawResult = res.data;
            if (rawResult && rawResult.data && Array.isArray(rawResult.data)) {
                rawResult = rawResult.data;
            }
            const data = Array.isArray(rawResult) ? rawResult[0] : rawResult;

            if (data && (data.idcaja || data.id)) {
                const idCaja = data.idcaja || data.id;
                // Guardar idcaja en localStorage directamente
                localStorage.setItem('idcaja', String(idCaja));

                const apertura: AperturaCaja = {
                    monto: Number(data.base_pesos) || Number(data.monto) || 0,
                    hora: nowTime(),
                    fecha: data.fecha_apertura || data.fecha || nowDate(),
                    usuario: String(data.idusuario || data.usuario || localStorage.getItem('auth_mde_us') || 'Usuario'),
                    observaciones: data.obser_apertura || data.observaciones || '',
                    idApertura: Number(idCaja),
                    idcaja: Number(idCaja),
                    idsede: Number(data.idsede) || idsede,
                };

                const nuevoEstado = {
                    cajaAbierta: true,
                    apertura,
                    movimientos: [],
                };

                setEstado(nuevoEstado);
                localStorage.setItem(STORAGE_KEY, JSON.stringify(nuevoEstado));
            } else {
                localStorage.removeItem('idcaja');
                const nuevoEstado = {
                    cajaAbierta: false,
                    apertura: null,
                    movimientos: [],
                };
                setEstado(nuevoEstado);
                localStorage.setItem(STORAGE_KEY, JSON.stringify(nuevoEstado));
            }
        } catch (error) {
            console.error('Error al verificar caja abierta en servidor:', error);
        }
    }, []);

    // Consultar caja abierta en el backend al montar en /monitor2
    useEffect(() => {
        verificarCajaServidor();
    }, [verificarCajaServidor]);

    // ── Abrir caja ─────────────────────────────────────────────────────────
    const abrirCaja = useCallback(async (monto: number, observaciones?: string, bancoini?: number) => {
        setLoadingCaja(true);
        try {
            const idempresa = Number(localStorage.getItem('auth_mde_emp')) || 1;
            const idsede = Number(localStorage.getItem('auth_mde_sede')) || 1;
            const idusuario = Number(localStorage.getItem('auth_mde_us')) || 1;
            const usuarioStr = localStorage.getItem('auth_mde_nombre') || localStorage.getItem('auth_mde_us') || 'Usuario';
            const fecha = nowDate();
            const hora = nowTime();

            const payload = {
                idempresa,
                idsede,
                idusuario,
                baseinicial: monto,
                obser: observaciones || '',
                bancoini: bancoini ?? null
            };

            const res = await axiosInstance.post('/caja/abrecaja', payload);

            let rawResult = res.data;
            if (rawResult && rawResult.data && Array.isArray(rawResult.data)) {
                rawResult = rawResult.data;
            }
            const data = Array.isArray(rawResult) ? rawResult[0] : (rawResult?.data || rawResult);
            const idCaja = data?.idcaja || data?.id || rawResult?.idcaja;

            if (idCaja) {
                localStorage.setItem('idcaja', String(idCaja));
            }

            const apertura: AperturaCaja = {
                monto,
                hora,
                fecha: data?.fecha_apertura || fecha,
                usuario: String(usuarioStr),
                observaciones: observaciones || '',
                idApertura: idCaja ? Number(idCaja) : undefined,
                idcaja: idCaja ? Number(idCaja) : undefined,
                idsede,
            };

            const nuevoEstado = {
                cajaAbierta: true,
                apertura,
                movimientos: [],
            };

            setEstado(nuevoEstado);
            localStorage.setItem(STORAGE_KEY, JSON.stringify(nuevoEstado));
        } catch (error) {
            console.error("Error al abrir caja en backend:", error);
            throw error;
        } finally {
            setLoadingCaja(false);
        }
    }, []);

    // ── Cerrar caja ────────────────────────────────────────────────────────
    const cerrarCaja = useCallback(async () => {
        setLoadingCaja(true);
        try {
            // TODO: Descomentar y ajustar la ruta real del backend
            // import axiosInstance from '../services/axios.instance';
            // await axiosInstance.post('/caja/cerrar', {
            //     idempresa: Number(localStorage.getItem('auth_mde_emp')),
            //     idApertura: estado.apertura?.idApertura,
            //     movimientos: estado.movimientos,
            //     totalRecaudado: estado.movimientos.reduce((acc, m) => acc + m.totalCobrado, 0),
            // });

            await Promise.resolve(); // placeholder

            setEstado(estadoInicial);
        } finally {
            setLoadingCaja(false);
        }
    }, [estado]);

    // ── Registrar pago ─────────────────────────────────────────────────────
    const registrarPago = useCallback(async (
        movimiento: Omit<MovimientoCaja, 'id' | 'fecha' | 'hora'>
    ): Promise<MovimientoCaja> => {
        setLoadingCaja(true);
        try {
            // TODO: Descomentar y ajustar la ruta real del backend
            // import axiosInstance from '../services/axios.instance';
            // await axiosInstance.post('/caja/movimiento', {
            //     idempresa: Number(localStorage.getItem('auth_mde_emp')),
            //     idApertura: estado.apertura?.idApertura,
            //     ...movimiento,
            //     fecha: nowDate(),
            //     hora: nowTime(),
            // });

            await Promise.resolve(); // placeholder

            const nuevoMov: MovimientoCaja = {
                ...movimiento,
                id: genId(),
                fecha: nowDate(),
                hora: nowTime(),
            };

            setEstado(prev => ({
                ...prev,
                movimientos: [...prev.movimientos, nuevoMov],
            }));

            return nuevoMov;
        } finally {
            setLoadingCaja(false);
        }
    }, [estado]);

    return (
        <CajaContext.Provider value={{ ...estado, abrirCaja, cerrarCaja, registrarPago, loadingCaja, verificarCajaServidor }}>
            {children}
        </CajaContext.Provider>
    );
};

export default CajaContext;
